import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import { Footprints, Mail, MessageCircle, Phone, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { useOrg, can } from "../org";
import { Initials, displayName } from "../people/bits";
import { addDaysISO, churchToday, isoToPickerDate } from "./text";
import { VoiceRecorder, VoiceReview, VoiceStatus, discardVoice, saveVoice, useVoiceProgress } from "./Voice";

export const METHODS = [
  { id: "phone", label: "Phone", Icon: Phone },
  { id: "text", label: "Text", Icon: MessageCircle },
  { id: "visit", label: "Visit", Icon: Footprints },
  { id: "in_person", label: "In person", Icon: Users },
  { id: "email", label: "Email", Icon: Mail },
];
const FOLLOW = [{ id: "1", label: "Tomorrow", days: 1 }, { id: "3", label: "3 days", days: 3 }, { id: "7", label: "1 week", days: 7 }, { id: "date", label: "Choose date" }, { id: "none", label: "No follow-up" }];

const LogCareCtx = createContext(null);
export const useLogCare = () => useContext(LogCareCtx);

const Sheet = ({ onClose, children }) => (
  <>
    <motion.div className="fixed inset-0 z-40 bg-[rgba(24,18,12,0.38)] backdrop-blur-[2px]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="sheet-backdrop" />
    <motion.div role="dialog" aria-modal="true" data-testid="log-care-sheet"
      className="fixed z-50 bg-paper overflow-y-auto inset-x-0 bottom-0 max-h-[94dvh] rounded-t-[32px] lg:inset-x-auto lg:right-0 lg:top-0 lg:max-h-none lg:w-[520px] lg:rounded-none lg:border-l lg:border-line"
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 34, stiffness: 340 }}>
      <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-line lg:hidden" />
      {children}
    </motion.div>
  </>
);

const Success = ({ first, me, next, onDone }) => (
  <div className="flex flex-col items-center text-center py-10" data-testid="log-care-success">
    <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", damping: 12, stiffness: 200 }} className="h-24 w-24 rounded-full bg-calm-soft flex items-center justify-center">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none"><motion.path d="M12 25l8 8 16-17" stroke="var(--calm)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.2, duration: 0.45 }} /></svg>
    </motion.div>
    <h2 className="t-greeting text-[38px] mt-6" data-testid="log-care-success-title">Thank you, {me}.</h2>
    <p className="text-lg text-ink-soft mt-3 max-w-sm">Your care for {first} is saved.</p>
    <p className="text-lg text-ink mt-1 max-w-sm" data-testid="log-care-success-next">{next ? <>Next check-in: <b>{format(isoToPickerDate(next), "EEEE, MMM. d")}</b>. It'll be on your Today list.</> : <>No follow-up scheduled. {first} stays in your care.</>}</p>
    <button onClick={onDone} className="btn btn-lg mt-10 px-10 bg-ink text-paper" data-testid="log-care-done-btn">Done</button>
  </div>
);

const Form = ({ p, preset, onSave, busy }) => {
  const { api, membership } = useOrg();
  const [method, setMethod] = useState(preset || null);
  const [note, setNote] = useState("");
  const [follow, setFollow] = useState(null);
  const [date, setDate] = useState(null);
  const [voice, setVoice] = useState(null);
  const days = FOLLOW.find((f) => f.id === follow)?.days;
  const nextISO = follow === "date" ? (date ? format(date, "yyyy-MM-dd") : null) : days ? addDaysISO(churchToday(), days) : null;
  const ready = method && follow && (follow !== "date" || date);
  const gotVoice = (v) => { setVoice({ ...v, suggestions: v.suggestions.map((s) => ({ ...s, decision: null })) }); if (v.summary) setNote((n) => n || v.summary); };
  useVoiceProgress(voice, gotVoice);
  const reviewVoice = (v) => {
    const fu = v.suggestions.find((s) => s.type === "follow_up" && s.decision === "approved" && s.due_on);
    if (fu && fu.due_on > churchToday()) { setFollow("date"); setDate(isoToPickerDate(fu.due_on)); }
    setVoice(v);
  };
  const pending = voice?.suggestions.some((s) => s.decision === "approved" && s.type === "assignment" && !s.due_on);
  return (
    <div data-testid="log-care-form">
      <div className="flex items-center gap-3"><Initials p={p} size={48} /><div><p className="t-label">Log care for</p><p className="t-name-lg mt-0.5">{displayName(p)}</p></div></div>
      <h3 className="t-heading-sm mt-9">How did you connect?</h3>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {METHODS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setMethod(id)} aria-pressed={method === id} className="choice h-[76px] flex flex-col items-center justify-center gap-1.5" data-testid={`log-method-${id}`}><Icon size={20} />{label}</button>
        ))}
      </div>
      <h3 className="t-heading-sm mt-9">Anything the care team should know?</h3>
      {!voice ? <VoiceRecorder person={p} onResult={gotVoice} /> : (
        <div className="surface p-4 mt-4" data-testid="voice-attached">
          <div className="flex items-center justify-between"><p className="t-body font-medium">Voice note attached</p><button onClick={() => { discardVoice(api, voice); setVoice(null); }} className="text-sm text-ink-soft underline underline-offset-4" data-testid="voice-remove-btn">Remove</button></div>
          <VoiceStatus v={voice} onRetry={gotVoice} />
          <VoiceReview v={voice} setV={reviewVoice} allowed={can(membership).oversee ? undefined : ["follow_up", "care_need"]} />
        </div>
      )}
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={voice?.summary ? 5 : 3} placeholder="e.g. Feeling a bit better. Could use a meal this week." className="field mt-4 p-4 leading-relaxed resize-none placeholder:text-ink-faint" data-testid="log-care-note" />
      <p className="text-sm text-ink-faint mt-2">Shared with {p.first_name}'s care team. Private pastoral notes belong in Confidential.</p>
      <h3 className="t-heading-sm mt-9">Follow up again?</h3>
      <div className="flex flex-wrap gap-2 mt-4">
        {FOLLOW.map((f) => (
          <button key={f.id} onClick={() => setFollow(f.id)} aria-pressed={follow === f.id} className="choice h-12 px-5" data-testid={`log-follow-${f.id}`}>{f.id === "date" && date && follow === "date" ? format(date, "MMM d") : f.label}</button>
        ))}
      </div>
      {follow === "date" && (
        <div className="mt-3 surface inline-block" data-testid="log-follow-calendar"><Calendar mode="single" selected={date} onSelect={setDate} defaultMonth={isoToPickerDate(addDaysISO(churchToday(), 1))} disabled={{ before: isoToPickerDate(addDaysISO(churchToday(), 1)) }} /></div>
      )}
      <div className="sticky bottom-0 -mx-6 px-6 pt-6 pb-6 mt-4 bg-gradient-to-t from-paper via-paper to-transparent">
        {pending && <p className="t-meta mb-2" data-testid="voice-needs-date">Add a due date to each approved care assignment.</p>}
        <button disabled={!ready || busy || pending} onClick={() => onSave({ method, note, follow_up_on: nextISO }, voice)} className="btn btn-primary btn-lg w-full" data-testid="log-care-save-btn">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
};

export const LogCareProvider = ({ children }) => {
  const { api, me, membership } = useOrg();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => { if (state?.result) state.onSaved?.(); setState(null); };
  const approved = async (pid, voice) => {
    for (const [i, s] of voice.suggestions.entries()) {
      if (s.decision !== "approved") continue;
      const src = { voice_note_id: voice.id, suggestion_index: i };
      try {
        if (s.type === "care_need") await api(`/people/${pid}/needs`, { method: "POST", body: { title: s.title, ...src } });
        if (s.type === "assignment") await api(`/people/${pid}/assignments`, { method: "POST", body: { title: s.title, assignee_id: membership.id, due_on: s.due_on, ...src } });
      } catch (e) { toast.error(`${s.title}: ${e.message}`); }
    }
  };
  const save = async (body, voice) => {
    setBusy(true);
    try {
      const r = await api(`/people/${state.person.id}/care-log`, { method: "POST", body: { ...body, care_need_id: state.careNeedId || null, voice_note_id: voice?.id || null } });
      if (voice) {
        await saveVoice(api, voice, { interaction_id: r.interaction.id, decisions: voice.suggestions.map((s) => s.decision || null) }).catch((e) => toast.error(`Care saved, but the voice note wasn't attached: ${e.message}`));
        await approved(state.person.id, voice);
      }
      setState((s) => ({ ...s, result: r }));
      window.dispatchEvent(new Event("care:logged"));
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  const open = (person, opts = {}) => setState({ person, ...opts });
  return (
    <LogCareCtx.Provider value={open}>
      {children}
      <AnimatePresence>
        {state && (
          <Sheet onClose={close}>
            <div className="px-6 pt-4 pb-2">
              <div className="flex justify-end"><button onClick={close} aria-label="Close" className="h-11 w-11 rounded-full bg-sunk flex items-center justify-center text-ink-soft" data-testid="log-care-close-btn"><X size={18} /></button></div>
              {!state.result ? <Form p={state.person} preset={state.method} onSave={save} busy={busy} />
                : <Success first={state.result.person.first_name} me={me.user.name.split(" ")[0]} next={state.result.follow_up?.due_on} onDone={close} />}
            </div>
          </Sheet>
        )}
      </AnimatePresence>
    </LogCareCtx.Provider>
  );
};

export const ContactActions = ({ p, careNeedId, className }) => {
  const openLog = useLogCare();
  const reach = (method, href) => {
    if (href) window.location.href = href;
    toast(`${method === "phone" ? "Calling" : "Texting"} ${p.first_name}…`, { description: "When you're done, log it here.", action: { label: "Log it", onClick: () => openLog(p, { method, careNeedId }) } });
  };
  return (
    <div className={cn("grid grid-cols-[auto_auto_1fr] gap-2", className)}>
      <button className="btn btn-quiet" disabled={!p.phone} onClick={() => reach("phone", `tel:${p.phone}`)} data-testid={`call-${p.id}`}><Phone size={18} /> Call</button>
      <button className="btn btn-quiet" disabled={!p.phone} onClick={() => reach("text", `sms:${p.phone}`)} data-testid={`text-${p.id}`}><MessageCircle size={18} /> Text</button>
      <button className="btn btn-primary" onClick={() => openLog(p, { careNeedId })} data-testid={`log-care-${p.id}`}>Log care</button>
    </div>
  );
};
